/**
 * TopDownLadder — the ONE top-down read, drawn the way the operator reads a chart.
 *
 * Source: learning-engine GET /api/v2/ml/analytics/tf-introspection?symbol=  (the world-snapshot
 * facets: htf_bias, top_down, story, timeframes). Dashboard and Journal both use this component,
 * so the two pages can never show two different reads of the same market (the Journal used to read
 * /ml/world-state, a different model, and could disagree with the Dashboard).
 *
 * The ladder is read MN1 → W1 → D1 → H4 → H1 → M15 → M5. Each timeframe is a band nested inside the
 * timeframe it is MADE of on the calendar (a higher candle is made of the lower ones), coloured by its
 * direction, and labelled with its relation to the higher-timeframe bias. A week can straddle two months,
 * so W1 is NOT drawn inside MN1: both sit at the top and D1 is nested in each (NEST_DEPTH).
 */
import { useCallback, useEffect, useState } from 'react';

export const LADDER = ['MN1', 'W1', 'D1', 'H4', 'H1', 'M15', 'M5'] as const;
/** Calendar nesting depth: W1 is not inside MN1 (a week straddles two months), D1 is inside both. */
export const NEST_DEPTH: Record<string, number> = { Y1: 0, MN1: 0, W1: 0, D1: 1, H4: 2, H1: 3, M30: 4, M15: 4, M5: 5, M2: 6, M1: 6 };

export interface TfRow {
  timeframe: string; trend?: string; structure_trend?: string; last_labels?: string[] | null;
  range?: { zone?: string | null } | null; last_candle?: { name?: string | null } | null;
}
export interface TopDownStep { timeframe: string; relation: string; direction: string | null; zone?: string | null; last_labels?: string[] | null }
export interface TopDownRead {
  symbol?: string; htf_bias: string | null; htf_bias_timeframe?: string | null;
  timeframes: TfRow[]; top_down?: TopDownStep[]; story?: string[];
  reversal_watch?: { timeframe: string; took: string; stage: string; shifted?: string[] }[];
  snapshot_id?: string | null; source?: string;
}
export type TopDownSource = { state: 'loading' | 'ok' | 'offline'; data: TopDownRead | null; error?: string };

const URL_BASE = '/svc/learning/api/v2/ml/analytics/tf-introspection';

/** Fetch the read; `reloadKey` changing (e.g. a bus event counter) re-reads it. */
export function useTopDown(symbol: string, reloadKey: unknown = 0): TopDownSource & { reload: () => void } {
  const [src, setSrc] = useState<TopDownSource>({ state: 'loading', data: null });
  const load = useCallback(() => {
    fetch(`${URL_BASE}?symbol=${encodeURIComponent(symbol)}`, { cache: 'no-store', signal: AbortSignal.timeout(20000) })
      .then(async r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (!Array.isArray(d?.timeframes) || d.timeframes.length === 0) throw new Error('no timeframes in the read');
        setSrc({ state: 'ok', data: d });
      })
      .catch(e => setSrc(s => ({ state: 'offline', data: s.data?.symbol === symbol ? s.data : null, error: String(e?.message ?? e) })));
  }, [symbol]);
  useEffect(() => { setSrc({ state: 'loading', data: null }); }, [symbol]);
  useEffect(() => { load(); }, [load, reloadKey]);
  return { ...src, reload: load };
}

const RELATION_TEXT: Record<string, string> = {
  sets_direction: 'sets the direction', with: 'with the trend', retracement: 'retracement',
  retracing: 'retracement', unknown: 'unknown',
  turning: 'turning against it', undecided: 'no direction yet',
};
const STAGE_TEXT: Record<string, string> = {
  swept_only: 'no lower timeframe has shifted yet; the move may simply continue',
  ltf_shift: 'lower timeframes have changed character against it',
  ltf_shift_with_displacement: 'lower timeframes have changed character and displaced against it; a new cycle may be starting',
};

function dirOf(row: TfRow | undefined, step: TopDownStep | undefined): 'up' | 'down' | null {
  const s = (step?.direction ?? row?.structure_trend ?? row?.trend ?? '').toString().toLowerCase();
  if (s === 'up' || s === 'bullish') return 'up';
  if (s === 'down' || s === 'bearish') return 'down';
  return null;
}

const COLOR = { up: '#16a34a', down: '#dc2626', none: '#475569' };

export function TopDownLadder({ read, showStory = true }: { read: TopDownRead; showStory?: boolean }) {
  const rowsBy = new Map(read.timeframes.map(r => [String(r.timeframe).toUpperCase(), r]));
  const stepsBy = new Map((read.top_down ?? []).map(s => [String(s.timeframe).toUpperCase(), s]));
  // The ladder is fixed; a timeframe the engine did not read is drawn greyed, not hidden.
  const extra = read.timeframes.map(r => String(r.timeframe).toUpperCase()).filter(tf => !(LADDER as readonly string[]).includes(tf));
  const ladder = [...LADDER, ...extra];
  const biasTf = (read.htf_bias_timeframe ?? '').toUpperCase();
  const bias = (read.htf_bias ?? '').toLowerCase();
  const biasColor = bias === 'bullish' ? COLOR.up : bias === 'bearish' ? COLOR.down : COLOR.none;

  const W = 640, ROW = 30, INDENT = 22, LABEL = 46;
  const H = ladder.length * ROW + 4;

  return (
    <div className="tdl">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '10px 16px 4px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 22, fontWeight: 650, color: biasColor }}>{read.htf_bias ?? 'No direction'}</span>
        <span style={{ fontSize: 12, color: 'var(--mil-text-tertiary, #64748b)' }}>
          {read.htf_bias_timeframe ? `higher-timeframe bias set by ${read.htf_bias_timeframe}` : 'no timeframe has a direction yet'}
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" style={{ display: 'block', padding: '0 8px' }}
           aria-label={`Top-down ladder: ${read.htf_bias ?? 'no direction'}`}>
        {ladder.map((tf, i) => {
          const row = rowsBy.get(tf);
          const step = stepsBy.get(tf);
          const d = dirOf(row, step);
          const read_ = !!row || !!step;
          const x = LABEL + (NEST_DEPTH[tf] ?? i) * INDENT;
          const y = i * ROW + 2;
          const w = W - x - 4;
          const fill = !read_ ? 'transparent' : d === 'up' ? COLOR.up : d === 'down' ? COLOR.down : COLOR.none;
          const rel = step?.relation;
          const relText = !read_ ? 'not read' : rel ? (RELATION_TEXT[rel] ?? rel) : (row?.structure_trend ?? row?.trend ?? '');
          const labels = (step?.last_labels ?? row?.last_labels ?? [])?.slice(-3).join(' ') ?? '';
          const zone = step?.zone ?? row?.range?.zone ?? '';
          const isBias = tf === biasTf;
          return (
            <g key={tf}>
              <text x={4} y={y + ROW / 2 + 4} fontSize={12} fontWeight={isBias ? 700 : 500}
                    fill={read_ ? 'var(--mil-text-primary, #e2e8f0)' : '#475569'}>{tf}</text>
              <rect x={x} y={y + 3} width={w} height={ROW - 6} rx={4}
                    fill={fill} fillOpacity={read_ ? (rel === 'retracement' || rel === 'retracing' ? 0.14 : 0.24) : 0}
                    stroke={read_ ? fill : '#334155'} strokeOpacity={read_ ? 0.9 : 0.6}
                    strokeDasharray={read_ ? (rel === 'retracement' || rel === 'retracing' ? '4 3' : undefined) : '2 3'}
                    strokeWidth={isBias ? 2 : 1} />
              <text x={x + 8} y={y + ROW / 2 + 4} fontSize={12} fill={read_ ? fill === 'transparent' ? '#64748b' : '#f8fafc' : '#475569'}>
                {d === 'up' ? '▲' : d === 'down' ? '▼' : '●'} {relText}
              </text>
              <text x={W - 10} y={y + ROW / 2 + 4} fontSize={11} textAnchor="end" fill="#94a3b8">
                {[labels, zone].filter(Boolean).join(' · ')}
              </text>
            </g>
          );
        })}
      </svg>
      {(read.reversal_watch ?? []).map(w => (
        <div className="db-watch" key={w.timeframe}>
          <strong>{w.timeframe} took the {w.took}.</strong> {STAGE_TEXT[w.stage] ?? w.stage}{w.shifted?.length ? ` (${w.shifted.join(', ')})` : ''}.
        </div>
      ))}
      {showStory && (read.story?.length ?? 0) > 0 && (
        <ol style={{ margin: '6px 16px 12px', paddingLeft: 18, fontSize: 12, lineHeight: 1.55, color: 'var(--mil-text-secondary, #94a3b8)' }}>
          {read.story!.map((line, i) => <li key={i}>{line}</li>)}
        </ol>
      )}
    </div>
  );
}
